import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useContext, useEffect, useState } from "react";
import { fetchQuery, useMutation, useRelayEnvironment } from "react-relay";
import {
  ScreensTextGenerationInput,
  useBrandGenerateScreensAsyncMutation,
} from "./__generated__/useBrandGenerateScreensAsyncMutation.graphql";
import {
  useBrandGenerateScreensAsyncMutationGetStatusQuery,
  useBrandGenerateScreensAsyncMutationGetStatusQuery$data,
} from "./__generated__/useBrandGenerateScreensAsyncMutationGetStatusQuery.graphql";
import SnackbarContext from "../contexts/SnackbarContext";

const generateScreensContentMutationTask = graphql`
  mutation useBrandGenerateScreensAsyncMutation(
    $input: ScreensTextGenerationInput!
  ) {
    startScreensGenerationTask(input: $input) {
      taskKey
      status
      message
    }
  }
`;

const screensGenerationStatusQuery = graphql`
  query useBrandGenerateScreensAsyncMutationGetStatusQuery($taskKey: String!) {
    screensGenerationTaskStatus(taskKey: $taskKey) {
      status
      error
      result {
        notificationOptIn {
          title
          subtitle
          backgroundImage
        }
        onboarding {
          rootTitle
          upsellTitle
          upsellSubtitle
          backgroundImage
        }
        account {
          deletionDialogTitle
          deletionSubtitle
          registerTitle
          registerSubtitle
          signInTitle
          signInSubtitle
        }
        automation {
          abandonedCartNotifications {
            title
            message
          }
          abandonedBrowseNotifications {
            title
            message
          }
          abandonedCheckoutNotifications {
            title
            message
          }
          welcomeSeriesNotifications {
            title
            message
            landingPath
          }
          reengagementNotifications {
            title
            message
            landingPath
          }
        }
        appListing {
          appSubtitle
          promotionalText
          appDescription
          keywords
          appClipSubtitle
          appClipButtonText
          primaryCategory
          secondaryCategory
          appClipHeader
          appIcon
        }
      }
    }
  }
`;

export type ScreensGeneratedContentWithImages =
  useBrandGenerateScreensAsyncMutationGetStatusQuery$data["screensGenerationTaskStatus"]["result"];

const useBrandGenerateScreensContentAsyncMutation = (): {
  generateScreensContentAsync: (
    input: ScreensTextGenerationInput,
    onErrorMessages?: (
      errors: Array<{
        field: string;
        message: string;
      }>
    ) => void
  ) => void;
  isMutationInFlight: boolean;
  isGenerating: boolean;
  generatedData: ScreensGeneratedContentWithImages | null;
} => {
  const [saveMutation, isMutationInFlight] =
    useMutation<useBrandGenerateScreensAsyncMutation>(
      generateScreensContentMutationTask
    );
  const environment = useRelayEnvironment();
  const snackbarContext = useContext(SnackbarContext);

  const [taskKey, setTaskKey] = useState<string | null>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedData, setGeneratedData] =
    useState<ScreensGeneratedContentWithImages | null>(null);

  // Fetch the status of the generation task
  useEffect(() => {
    if (!taskKey || !isGenerating) return;

    const fetchStatusInterval = setInterval(async () => {
      try {
        const data =
          await fetchQuery<useBrandGenerateScreensAsyncMutationGetStatusQuery>(
            environment,
            screensGenerationStatusQuery,
            { taskKey },
            { fetchPolicy: "network-only" }
          ).toPromise();

        const statusData = data?.screensGenerationTaskStatus;
        if (statusData) {
          if (statusData.status === "SUCCESS") {
            setGeneratedData(statusData.result);
            setIsGenerating(false);
          } else if (statusData.status === "FAILURE") {
            snackbarContext?.openSnackbar(
              `Screens content generation failed: ${
                statusData.error || "Unknown error"
              }`,
              "error"
            );
            setIsGenerating(false);
          }
        }
      } catch (error) {
        console.error("Error fetching status:", error);
      }
    }, 5000);

    return () => clearInterval(fetchStatusInterval);
  }, [taskKey, isGenerating, environment, snackbarContext]);

  const onMutate = useCallback(
    (
      input: ScreensTextGenerationInput,
      onErrorMessages?: (
        errors: Array<{
          field: string;
          message: string;
        }>
      ) => void
    ) => {
      saveMutation({
        variables: {
          input,
        },
        onCompleted: (data, errors) => {
          if (errors) {
            const handledErrors = errors
              .filter((error) => error.message)
              .map((error) => ({
                field: "screensContent",
                message: error.message,
              }));

            snackbarContext?.openSnackbar(
              "Failed to generate screens content",
              "error"
            );
            onErrorMessages && onErrorMessages(handledErrors);
          } else {
            // Start fetching the status if we have a task key
            if (data.startScreensGenerationTask?.taskKey) {
              setTaskKey(data.startScreensGenerationTask.taskKey);
              setIsGenerating(true);
            }
          }
        },
        onError: (error: Error) => {
          snackbarContext?.openSnackbar(
            "Failed to generate screens content",
            "error"
          );
        },
      });
    },
    [snackbarContext, saveMutation]
  );

  return {
    generateScreensContentAsync: onMutate,
    isMutationInFlight,
    isGenerating,
    generatedData,
  };
};

export default useBrandGenerateScreensContentAsyncMutation;
